import { computed, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { catchError, finalize, of, take } from 'rxjs';
import { AfmBeService } from 'src/app/services/afm-be/afm-be.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { LoadingBarService } from 'src/app/services/loading-bar/loading-bar.service';
import { TopItemsService } from 'src/app/services/top-items/top-items.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { getAllCraftingLocations, getAllLocations, getAllMarketLocations, getAllRoyalMarketLocations } from 'src/app/shared/constants/locations.constant';
import { getAllPriceTypes, RRR } from 'src/app/shared/constants/market.constant';
import { Server, SERVERS } from 'src/app/shared/constants/servers.constant';
import { Achievement } from 'src/app/shared/models/achievement.model';
import { CraftGroup, DoneCraft } from 'src/app/shared/models/craft-models/craft-models';
import { CraftGroupPersistenceHelper, fromDoneCraftPersistenceModel } from 'src/app/shared/models/craft-models/craft-persistence-models';
import { SpecLevel } from 'src/app/shared/models/craft-models/specs-models';
import { Item } from 'src/app/shared/models/items.model';
import { Location } from 'src/app/shared/models/location.model';
import { PriceType } from 'src/app/shared/models/market.model';

@Injectable({
  providedIn: 'root',
})
export class CraftCalculatorStateService {
  public craftGroup: WritableSignal<CraftGroup>;
  public servers: Server[] = SERVERS;
  public RRR = RRR;
  public tiers = [1, 2, 3, 4, 5, 6, 7, 8];
  public enchantments = [0, 1, 2, 3, 4];
  public craftLocations: Location[] = getAllCraftingLocations();
  public marketLocations: Location[] = getAllMarketLocations();
  public royalMarketLocations: Location[] = getAllRoyalMarketLocations();
  public priceTypes = getAllPriceTypes();
  public craftableItems: Signal<Item[]>;
  public itemsSignal: Signal<Item[]>;
  public achievementsSignal: Signal<Achievement[]>;

  // Loading State
  public disableButtons: WritableSignal<boolean> = signal(false);
  public isUsingTheServer = computed(() => {
    return (
      this.isDeletingDoneCraft() ||
      this.isSavingCraftGroup() ||
      this.isSavingDoneCraft() ||
      this.isLoadingUserCraftGroups() ||
      this.isLoadingUserDoneCrafts() ||
      this.isLoadingSpecs() ||
      this.isSavingSpecs() ||
      this.isDeletingCraftGroup() ||
      this.isRefreshingPrices()
    );
  });
  public isLoadingUserCraftGroups = signal(false);
  public isLoadingUserDoneCrafts = signal(false);
  public isSavingCraftGroup = signal(false);
  public isSavingDoneCraft = signal(false);
  public isDeletingDoneCraft = signal(false);
  public isDeletingCraftGroup = signal(false);
  public isLoadingSpecs = signal(false);
  public isSavingSpecs = signal(false);
  public isRefreshingPrices = signal(false);

  // Has Loaded States
  public hasLoadedCraftGroups = signal(false);
  public hasLoadedDoneCrafts = signal(false);
  public hasLoadedSpecs = signal(false);

  // Setup State
  public oldCraftsSort: MatSort | null = null;
  public craftsSelectedTiers = signal<number[]>([]);
  public craftsSelectedEnchantments = signal<number[]>([]);

  // Input State
  public oldInputSort: MatSort | null = null;

  // Output State
  public oldOutputSort: MatSort | null = null;

  // User Data
  public userCraftGroups = signal<CraftGroup[]>([]);
  public userDoneCrafts = signal<DoneCraft[]>([]);

  // User Specs
  public userSpecsLevels = signal<SpecLevel[]>([]);

  constructor(
    private jsonReaderService: JsonReaderService,
    private aoDataGetterService: AoDataGetterService,
    private userSettingsService: UserSettingsService,
    private topItemsService: TopItemsService,
    private afmBeService: AfmBeService,
    private loadingBarService: LoadingBarService,
    private snackBar: MatSnackBar
  ) {
    this.jsonReaderService.ensureLoaded();

    if (!this.hasLoadedCraftGroups()) {
      this.loadUserCraftGroup();
    }
    if (!this.hasLoadedDoneCrafts()) {
      this.loadUserDoneCrafts();
    }
    if (!this.hasLoadedSpecs()) {
      this.loadSpecs();
    }

    effect(() => {
      if (this.isUsingTheServer()) {
        this.disableButtons.set(true);
      } else {
        setTimeout(() => {
          this.disableButtons.set(false);
        }, 3000);
      }
    });

    effect(() => {
      this.loadingBarService.setLoading(this.isUsingTheServer());
    });

    this.itemsSignal = toSignal(this.jsonReaderService.itemsData$, { initialValue: [] });
    this.achievementsSignal = toSignal(this.jsonReaderService.achievementsData$, { initialValue: [] });

    this.craftGroup = signal(new CraftGroup(this.jsonReaderService, this.aoDataGetterService, this.achievementsSignal, this.userSpecsLevels));
    this.craftGroup().server.set(this.userSettingsService.getUserServer());

    this.craftableItems = computed(() => {
      const allCraftableItems = this.itemsSignal().filter((item) => !!item.localizedName && item.craftingRequirements.length > 0 && (item.enchantmentLevel === 0 || item.enchantmentLevel === undefined));

      allCraftableItems.sort((a, b) => {
        const aSort = a.groupUniqueName + (a.tier ?? 0) + (a.enchantmentLevel ?? 0);
        const bSort = b.groupUniqueName + (b.tier ?? 0) + (b.enchantmentLevel ?? 0);
        return aSort.localeCompare(bSort);
      });

      return allCraftableItems;
    });
  }

  public resetCraftGroup() {
    this.craftGroup.set(new CraftGroup(this.jsonReaderService, this.aoDataGetterService, this.achievementsSignal, this.userSpecsLevels));
    this.craftGroup().server.set(this.userSettingsService.getUserServer());

    this.snackBar.open('Craft group has been reset.', undefined, { duration: 2000 });
  }

  public addCrafts(item: Item): void {
    this.itemsSignal()
      .filter((x) => x.groupUniqueName === item.groupUniqueName)
      .map((craftableItem) => {
        this.craftGroup().addCraft(craftableItem);
      });
  }

  public loadUserCraftGroup(): void {
    if (!this.afmBeService.userHasMasterpieceEntitlement()) {
      return;
    }

    this.isLoadingUserCraftGroups.set(true);

    this.topItemsService
      .getAllCraftGroups()
      .pipe(
        take(1),
        finalize(() => this.isLoadingUserCraftGroups.set(false)),
        catchError((error) => {
          console.error('Failed to load user craft groups:', error);
          this.snackBar.open('Error loading craft groups', 'Close', { duration: 4000 });
          return of([]);
        })
      )
      .subscribe((craftGroupPersistenceModels) => {
        if (craftGroupPersistenceModels.length > 0) {
          this.userCraftGroups.set(
            craftGroupPersistenceModels.map((persistenceModel) =>
              CraftGroupPersistenceHelper.fromPersistenceModel(persistenceModel, this.jsonReaderService, this.aoDataGetterService, this.achievementsSignal, this, getAllLocations(), getAllPriceTypes())
            )
          );
          this.snackBar.open('Loaded craft groups', undefined, { duration: 2000 });
        }
        this.hasLoadedCraftGroups.set(true);
      });
  }

  public loadUserDoneCrafts(): void {
    if (!this.afmBeService.userHasMasterpieceEntitlement()) {
      return;
    }

    this.isLoadingUserDoneCrafts.set(true);

    this.topItemsService
      .getAllDoneCrafts()
      .pipe(
        take(1),
        finalize(() => this.isLoadingUserDoneCrafts.set(false)),
        catchError((error) => {
          console.error('Failed to load user done crafts:', error);
          this.snackBar.open('Error loading done crafts', 'Close', { duration: 4000 });
          return of([]);
        })
      )
      .subscribe((doneCrafts) => {
        if (doneCrafts.length > 0) {
          this.userDoneCrafts.set(
            doneCrafts.map((doneCraft) => {
              return fromDoneCraftPersistenceModel(doneCraft, this.jsonReaderService, this.aoDataGetterService, this.achievementsSignal, this, getAllLocations(), getAllPriceTypes());
            })
          );
          this.snackBar.open('Loaded done crafts', undefined, { duration: 2000 });
        }
        this.hasLoadedDoneCrafts.set(true);
      });
  }

  public updateUserCraftGroups(craftGroup: any, id: string) {
    this.userCraftGroups.update((groups) => {
      const existingIndex = groups.findIndex((group) => group.id() === id);
      if (existingIndex !== -1) {
        groups[existingIndex] = craftGroup;
      } else {
        groups.push(craftGroup);
      }
      return [...groups];
    });
  }

  loadSpecs(): void {
    if (!this.afmBeService.userHasMasterpieceEntitlement()) {
      return;
    }
    this.isLoadingSpecs.set(true);

    this.topItemsService
      .getAllUserSpecs()
      .pipe(
        take(1),
        finalize(() => this.isLoadingSpecs.set(false))
      )
      .subscribe({
        next: (specsLevels) => {
          this.userSpecsLevels.set(specsLevels);
          this.forceFocusTrigger();
          this.hasLoadedSpecs.set(true);
          this.snackBar.open('Loaded specs levels', undefined, { duration: 2000 });
        },
        error: (error) => {
          console.error('Error loading specs levels:', error);
          this.snackBar.open('Error loading specs levels', 'Close', { duration: 4000 });
        },
      });
  }

  // This is a cheat to force the focus efficiency to be recalculated
  forceFocusTrigger(): void {
    this.craftGroup()
      .crafts()
      .forEach((craft) => {
        craft.focusTrigger.set(!craft.focusTrigger());
      });
  }

  async refreshCraftGroupPrices(): Promise<void> {
    this.isRefreshingPrices.set(true);
    try {
      await this.craftGroup().refreshPrices();
      this.snackBar.open('Refreshed craft group prices', undefined, { duration: 2000 });
    } catch (error) {
      console.error('Error refreshing craft group prices:', error);
      this.snackBar.open('Error refreshing prices', 'Close', { duration: 4000 });
    } finally {
      this.isRefreshingPrices.set(false);
    }
  }

  // MARK: propagations
  public propagateLocation(location: Location, type: 'input' | 'output', groupUniqueName?: string) {
    const craftGroupInstance = this.craftGroup();

    if (type === 'input') {
      craftGroupInstance.inputItems().forEach((inputItem) => {
        if (!groupUniqueName || inputItem.item.groupUniqueName === groupUniqueName) {
          inputItem.buyLocation.set(location);
        }
      });
    } else if (type === 'output') {
      craftGroupInstance.outputItems().forEach((outputItem) => {
        if (!groupUniqueName || outputItem.item.groupUniqueName === groupUniqueName) {
          outputItem.sellLocation.set(location);
        }
      });
    }
  }
  public propagatePriceType(priceType: PriceType, type: 'input' | 'output' | 'leftOvers', groupUniqueName?: string) {
    const craftGroupInstance = this.craftGroup();

    switch (type) {
      case 'input':
        craftGroupInstance.inputItems().forEach((inputItem) => {
          if (!groupUniqueName || inputItem.item.groupUniqueName === groupUniqueName) {
            inputItem.buyPriceType.set(priceType);
          }
        });
        break;
      case 'output':
        craftGroupInstance.outputItems().forEach((outputItem) => {
          if (!groupUniqueName || outputItem.item.groupUniqueName === groupUniqueName) {
            outputItem.sellPriceType.set(priceType);
          }
        });
        break;
      case 'leftOvers':
        craftGroupInstance.inputItems().forEach((inputItem) => {
          if (!groupUniqueName || inputItem.item.groupUniqueName === groupUniqueName) {
            inputItem.leftOverSellPriceType.set(priceType);
          }
        });
        break;
    }
  }
}
